import { zodResolver } from '@hookform/resolvers/zod';
import {
  INetsuite,
  NETSUITE_CUSTOMER_LOOKUP_OPTIONS,
  NETSUITE_PRODUCT_EXT_ID_OPTIONS,
  NetsuiteCustomerLookupEnum,
  NetsuiteProductExtIdEnum,
  NetsuiteSchema,
} from '@monetize/types/app';
import { Controller, useForm } from 'react-hook-form';
import { useSaveNetsuiteConfiguration } from '../../../../../api/accountingService';
import { handleApiErrorToast } from '../../../../../api/axios';
import {
  MButton,
  MDivider,
  MFormField,
  MGrid,
  MGridItem,
  MHStack,
  MInput,
} from '../../../../../components/Monetize';
import { MCustomSelect } from '../../../../../components/Monetize/MCustomSelect/MCustomSelect';
import { logger } from '../../../../../services/logger';
import { NETSUITE_FORM_FIELDS } from './accounting.constants';

interface NetsuiteFormProps {
  connection?: INetsuite | null;
  onClose: () => void;
}

export const NetsuiteForm = ({ connection, onClose }: NetsuiteFormProps) => {
  const { mutate: saveConnection, isPending: isLoading } =
    useSaveNetsuiteConfiguration({
      onError: (err) => {
        handleApiErrorToast(err);
      },
      onSuccess: () => {
        onClose();
      },
    });

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<INetsuite>({
    resolver: zodResolver(NetsuiteSchema),
    mode: 'onChange',
    defaultValues: {
      netsuiteProductExternalId: NetsuiteProductExtIdEnum.id,
      accountId: '',
      companyId: '',
      consumerKey: '',
      consumerSecret: '',
      tokenId: '',
      tokenSecret: '',
      ...connection,
      enabled: !connection ? true : connection.enabled,
      customerLookup: connection?.customerLookup
        ? connection.customerLookup
        : NetsuiteCustomerLookupEnum.NONE,
    },
  });

  const onSubmitHandler = async (data: INetsuite) => {
    saveConnection({
      ...data,
      customerLookup:
        data.customerLookup === NetsuiteCustomerLookupEnum.NONE
          ? null
          : data.customerLookup,
    });
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmitHandler, (error) => logger.error(error))}
      style={{ width: '100%' }}
    >
      <MGrid templateColumns="repeat(12, 1fr)" gap={4}>
        <MGridItem colSpan={6}>
          <MFormField
            error={errors.accountId}
            label={NETSUITE_FORM_FIELDS.accountId.label}
            isRequired
          >
            <Controller
              name="accountId"
              control={control}
              render={({ field }) => (
                <MInput {...field} isDisabled={isLoading} />
              )}
            />
          </MFormField>
        </MGridItem>
        <MGridItem colSpan={6}>
          <MFormField
            error={errors.companyId}
            label={NETSUITE_FORM_FIELDS.companyId.label}
            isRequired
          >
            <Controller
              name="companyId"
              control={control}
              render={({ field }) => (
                <MInput {...field} isDisabled={isLoading} />
              )}
            />
          </MFormField>
        </MGridItem>
        <MGridItem colSpan={6}>
          <MFormField
            error={errors.consumerKey}
            label={NETSUITE_FORM_FIELDS.consumerKey.label}
            isRequired
          >
            <Controller
              name="consumerKey"
              control={control}
              render={({ field }) => (
                <MInput {...field} isDisabled={isLoading} />
              )}
            />
          </MFormField>
        </MGridItem>
        <MGridItem colSpan={6}>
          <MFormField
            error={errors.consumerSecret}
            label={NETSUITE_FORM_FIELDS.consumerSecret.label}
            isRequired
          >
            <Controller
              name="consumerSecret"
              control={control}
              render={({ field }) => (
                <MInput {...field} isDisabled={isLoading} />
              )}
            />
          </MFormField>
        </MGridItem>
        <MGridItem colSpan={6}>
          <MFormField
            error={errors.tokenId}
            label={NETSUITE_FORM_FIELDS.tokenId.label}
            isRequired
          >
            <Controller
              name="tokenId"
              control={control}
              render={({ field }) => (
                <MInput {...field} isDisabled={isLoading} />
              )}
            />
          </MFormField>
        </MGridItem>
        <MGridItem colSpan={6}>
          <MFormField
            error={errors.tokenSecret}
            label={NETSUITE_FORM_FIELDS.tokenSecret.label}
            isRequired
          >
            <Controller
              name="tokenSecret"
              control={control}
              render={({ field }) => (
                <MInput {...field} isDisabled={isLoading} />
              )}
            />
          </MFormField>
        </MGridItem>
        <MGridItem colSpan={6}>
          <MFormField
            error={errors.customerLookup}
            label={NETSUITE_FORM_FIELDS.customerLookup.label}
            tooltip={NETSUITE_FORM_FIELDS.customerLookup.tooltip}
            isRequired
          >
            <Controller
              name="customerLookup"
              control={control}
              render={({ field }) => (
                <MCustomSelect
                  items={NETSUITE_CUSTOMER_LOOKUP_OPTIONS}
                  {...field}
                  isDisabled={isLoading}
                />
              )}
            />
          </MFormField>
        </MGridItem>
        <MGridItem colSpan={6}>
          <MFormField
            error={errors.netsuiteProductExternalId}
            label={NETSUITE_FORM_FIELDS.netsuiteProductExternalId.label}
            tooltip={NETSUITE_FORM_FIELDS.netsuiteProductExternalId.tooltip}
            isRequired
          >
            <Controller
              name="netsuiteProductExternalId"
              control={control}
              render={({ field: { value, ...rest } }) => (
                <MCustomSelect
                  items={NETSUITE_PRODUCT_EXT_ID_OPTIONS}
                  value={value || NetsuiteCustomerLookupEnum.NONE}
                  isDisabled={isLoading}
                  {...rest}
                />
              )}
            />
          </MFormField>
        </MGridItem>
        <MGridItem colSpan={6}>
          <MFormField
            error={errors.taxItemId}
            label={NETSUITE_FORM_FIELDS.taxItemId.label}
            tooltip={NETSUITE_FORM_FIELDS.taxItemId.tooltip}
          >
            <Controller
              name="taxItemId"
              control={control}
              render={({ field: { value, ...rest } }) => (
                <MInput value={value || ''} {...rest} isDisabled={isLoading} />
              )}
            />
          </MFormField>
        </MGridItem>
      </MGrid>
      <MDivider my={4} />
      <MHStack gap="4" justify="end" align="center">
        <MButton variant="cancel" onClick={onClose} isDisabled={isLoading}>
          Cancel
        </MButton>
        <MButton type="submit" variant="primary" isLoading={isLoading}>
          {connection?.accountId ? 'Save' : 'Connect'}
        </MButton>
      </MHStack>
    </form>
  );
};
